<template>
  <div class="content-wrapper view-aptlist-manage p-0">
    <div class="content df-correction-page">
      <!-- Page header -->
      <div class="page-head">
        <div>
          <h1>
            <i class="fas fa-edit title-fa"></i>
            <span>แก้ไข DF</span>
          </h1>
        </div>
        <div
          class="head-meta"
          v-if="lastUpdated"
        >
          <span class="pill">
            <i class="fas fa-clock"></i> อัปเดตล่าสุด {{ lastUpdated }}
          </span>
        </div>
      </div>

      <div class="card df-card">
        <!-- Filter bar -->
        <div class="filter-bar">
          <div class="filter-grid">
            <!-- Date range -->
            <div class="field">
              <label><i class="fas fa-calendar"></i> ช่วงวันที่</label>
              <div
                class="date-range-wrap"
                :class="{ 'is-invalid': !dateRange[0] || !dateRange[1] }"
              >
                <date-picker
                  v-model="dateRange"
                  type="date"
                  range
                  format="DD MMM YY"
                  :shortcuts="dateRangeShortcuts"
                  :disabled-date="notAfterToday"
                  placeholder="เลือกวันที่ต้องการค้นหา"
                  input-class="df-date-input"
                  :clearable="false"
                ></date-picker>
                <span
                  class="date-quick"
                  @click="setToday"
                  >วันนี้</span
                >
              </div>
            </div>

            <!-- Doctor -->
            <div class="field">
              <label><i class="fas fa-user-md"></i> แพทย์</label>
              <div class="ctrl-wrap select-wrap">
                <select
                  class="ctrl"
                  v-model="selectedDoctor"
                >
                  <option :value="null">ทุกแพทย์</option>
                  <option
                    v-for="d in doctorsList"
                    :key="d.uid"
                    :value="d.fullName"
                  >
                    {{ d.fullName }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Search -->
            <div class="field">
              <label><i class="fas fa-search"></i> ค้นหา</label>
              <div class="ctrl-wrap">
                <i class="fas fa-search lead"></i>
                <input
                  class="ctrl"
                  type="search"
                  v-model.trim="searchTable"
                  placeholder="ชื่อคนไข้, DN, เลขที่ใบเสร็จ, แพทย์ หรือรายการรักษา"
                  data-testid="df-correction-search-input"
                />
                <i
                  v-if="searchTable"
                  class="fas fa-times-circle trail clearable"
                  @click="searchTable = ''"
                ></i>
              </div>
            </div>

            <!-- Actions -->
            <div class="field actions">
              <button
                class="btn btn-secondary"
                title="ล้างตัวกรอง"
                @click="resetFilters"
              >
                <i class="fas fa-undo"></i> ล้าง
              </button>
              <button
                class="btn btn-primary"
                @click="fetchReport"
              >
                <i class="fas fa-search"></i> แสดงข้อมูล
              </button>
            </div>
          </div>
        </div>

        <!-- Quick view tabs + totals -->
        <div
          v-if="receiptItemList.length > 0"
          class="quick-row"
        >
          <span class="label-text">มุมมอง:</span>
          <span
            v-for="tab in viewTabs"
            :key="tab.id"
            :class="['chip', { active: view === tab.id }]"
            @click="setView(tab.id)"
          >
            <span
              v-if="tab.dot"
              class="badge-dot"
              :style="{ background: tab.dot }"
            ></span>
            {{ tab.label }}
            <span class="chip-count">· {{ tab.count }}</span>
          </span>
        </div>

        <!-- Table toolbar -->
        <div
          v-if="receiptItemList.length > 0"
          class="table-toolbar"
        >
          <div class="left">
            <span
              >พบ <strong>{{ filteredItems.length }}</strong> รายการ</span
            >
            <span v-if="filteredItems.length > 0"
              >· แสดง
              <strong>{{ Math.min(perPage, filteredItems.length) }}</strong>
              ต่อหน้า</span
            >
          </div>
          <div class="right">
            <b-dropdown
              v-if="filteredItems.length > 0"
              size="sm"
              variant="outline-success"
              right
            >
              <template #button-content>
                <i class="fas fa-download"></i> Export
              </template>
              <b-dropdown-item @click="excelExport">
                <i class="fas fa-file-excel mr-2"></i>Excel
              </b-dropdown-item>
              <b-dropdown-item @click="pdfExport">
                <i class="fas fa-file-pdf mr-2"></i>PDF
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>

        <!-- Table -->
        <div
          v-if="filteredItems.length > 0"
          class="tbl-wrap"
        >
          <b-table
            id="dfCorrectionTable"
            ref="dfCorrectionTable"
            class="df-tbl my-0"
            small
            sort-icon-left
            head-variant="light"
            :busy="tableBusy"
            :items="filteredItems"
            :fields="fields"
            sort-by="creationDt"
            :sort-desc="true"
            :responsive="true"
            sticky-header="calc(100vh - 360px)"
            :per-page="perPage"
            :current-page="currentPage"
          >
            <template #cell(index)="row">{{
              (currentPage - 1) * perPage + row.index + 1
            }}</template>
            <template #cell(creationDt)="row">
              {{ formatDate(row.value) }}
            </template>
            <template #cell(time)="row">
              {{ formatTime(row.item.creationDt) }}
            </template>
            <template #cell(receiptNo)="row">
              <span class="mono">{{ row.value }}</span>
            </template>
            <template #cell(dn)="row">
              <span class="mono">{{ row.value }}</span>
            </template>
            <template #cell(patientFullName)="row">
              <span class="name">{{ row.value }}</span>
            </template>
            <template #cell(doctorFullName)="row">
              <div class="doctor-cell">
                <div class="avatar">{{ doctorInitials(row.value) }}</div>
                <span>{{ row.value }}</span>
              </div>
            </template>
            <template #cell(paid)="row">
              <span class="num">{{ formatMoney(row.value) }}</span>
            </template>
            <template #cell(previousPaidDf)="row">
              <span
                v-if="row.item.hasDfCorrection"
                class="num df-prev"
                >{{ formatMoney(row.item.previousPaidDf) }}</span
              >
              <span
                v-else
                class="muted-dash"
                >-</span
              >
            </template>
            <template #cell(paidDf)="row">
              <span
                class="num df-num"
                :class="{ 'df-corrected': row.item.hasDfCorrection }"
                >{{ formatMoney(row.value) }}</span
              >
            </template>
            <template #cell(paidCf)="row">
              <span class="num">{{ formatMoney(row.value) }}</span>
            </template>
            <template #cell(latestCorrectionDt)="row">
              <span
                v-if="row.item.latestCorrectionDt"
                class="last-edited"
                :title="
                  formatDate(row.item.latestCorrectionDt) +
                  ' ' +
                  formatTime(row.item.latestCorrectionDt)
                "
              >
                {{ formatDate(row.item.latestCorrectionDt) }}
                <span class="last-edited-time">{{
                  formatTime(row.item.latestCorrectionDt)
                }}</span>
              </span>
              <span
                v-else
                class="muted-dash"
                >-</span
              >
            </template>
            <template #cell(dfStatus)="row">
              <span
                v-if="row.item.hasDfCorrection"
                class="badge-pill badge-warn"
                @click="openHistoryModal(row.item)"
              >
                <span class="badge-dot"></span> แก้ไขแล้ว
              </span>
              <span
                v-else
                class="badge-pill badge-ok"
              >
                <span class="badge-dot"></span> ปกติ
              </span>
            </template>
            <template #cell(action)="row">
              <button
                class="btn btn-xs btn-outline"
                :disabled="isCorrectionDisabled(row.item)"
                :title="rowDisabledReason(row.item)"
                @click="openCorrectionModal(row.item)"
              >
                <i class="fas fa-edit"></i> แก้ไข DF
              </button>
              <button
                v-if="row.item.hasDfCorrection"
                class="btn btn-xs btn-outline-info btn-icon"
                title="ประวัติ"
                @click="openHistoryModal(row.item)"
              >
                <i class="fas fa-history"></i>
              </button>
            </template>
            <template #custom-foot>
              <b-tr
                variant="secondary"
                class="text-bold text-right"
              >
                <td
                  class="text-left"
                  colspan="8"
                >
                  รวม
                </td>
                <td>{{ formatMoney(sum("paid")) }}</td>
                <td></td>
                <td>{{ formatMoney(sum("paidDf")) }}</td>
                <td>{{ formatMoney(sum("paidCf")) }}</td>
                <td></td>
                <td></td>
                <td class="report-export-skip"></td>
              </b-tr>
            </template>
          </b-table>
        </div>

        <!-- Empty state -->
        <div
          v-else
          class="empty"
        >
          <div class="ic"><i class="fas fa-inbox"></i></div>
          <h4>ไม่มีข้อมูลให้แสดง</h4>
          <p>
            <template v-if="receiptItemList.length === 0"
              >เลือกช่วงวันที่และกด "แสดงข้อมูล" เพื่อโหลดรายการ</template
            >
            <template v-else
              >ลองเปลี่ยนช่วงวันที่ ล้างตัวกรอง หรือเลือกมุมมองอื่น</template
            >
          </p>
        </div>

        <!-- Footer / pager -->
        <div
          v-if="filteredItems.length > 0"
          class="tbl-footer"
        >
          <div>หน้า {{ currentPage }} จาก {{ totalPages }}</div>
          <b-pagination
            v-model="currentPage"
            :total-rows="filteredItems.length"
            :per-page="perPage"
            size="sm"
            first-number
            last-number
            pills
            limit="10"
            class="df-pager m-0"
          ></b-pagination>
        </div>
      </div>

      <div class="info-strip">
        <i class="fas fa-info-circle"></i>
        <span
          >การแก้ไข DF จะถูกบันทึกเป็นประวัติพร้อมเหตุผล
          และไม่กระทบต่อยอดใบเสร็จที่ออกไปแล้ว</span
        >
      </div>

      <DfCorrectionModal
        ref="dfCorrectionModal"
        @correctionSaved="onCorrectionSaved"
        @error="onError"
      />
      <DfCorrectionHistoryModal ref="dfCorrectionHistoryModal" />
      <Dialog ref="Dialog"></Dialog>
      <Loading v-if="isLoading"></Loading>
      <PdfExport ref="pdfExport" :watermark="true" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import DfCorrectionModal from "@/components/modal/DfCorrectionModal";
import DfCorrectionHistoryModal from "@/components/modal/DfCorrectionHistoryModal";
import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import PdfExport from "@/components/PdfExport";
import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";
import { matchesSearchQuery } from "@/utils/textSearch";
import moment from "moment";

// Phase 0 subscription gating: unsubscribed users can still correct DF
// entries, but only within a 7-day grace window from the receipt's creation
// date. Subscribed users have no time limit. Phase 1 will consolidate this
// into a shared SubscriptionMixin.
const DF_CORRECTION_GRACE_DAYS = 7;

export default {
  name: "DFCorrection",
  components: {
    DfCorrectionModal,
    DfCorrectionHistoryModal,
    Dialog,
    Loading,
    PdfExport,
  },
  data() {
    return {
      isLoading: false,
      tableBusy: false,
      selectedDoctor: null,
      dateRange: [],
      receiptItemList: [],
      searchTable: "",
      view: "all",
      lastUpdated: null,
      currentPage: 1,
      perPage: 100,
      fields: [
        { key: "index", label: "#", class: "col-idx" },
        {
          key: "creationDt",
          label: "วันที่",
          sortable: true,
          class: "narrow-spacing",
        },
        { key: "time", label: "เวลา", class: "narrow-spacing" },
        {
          key: "receiptNo",
          label: "เลขที่ใบเสร็จ",
          class: "narrow-spacing",
          // Force text type on Excel export. SheetJS reads "2569-0000NN" as
          // year-month, so receipts 001–012 (valid months) get turned into
          // dates. data-t="s" keeps the cell as plain text.
          tdAttr: { "data-t": "s" },
        },
        { key: "dn", label: "DN.", class: "narrow-spacing" },
        { key: "patientFullName", label: "ชื่อคนไข้", class: "narrow-spacing" },
        { key: "itemName", label: "รายการรักษา", class: "narrow-spacing" },
        { key: "doctorFullName", label: "แพทย์", class: "narrow-spacing" },
        { key: "paid", label: "ชำระ", class: "text-right" },
        { key: "previousPaidDf", label: "DF เดิม", class: "text-right" },
        { key: "paidDf", label: "DF", class: "text-right" },
        { key: "paidCf", label: "CF", class: "text-right" },
        {
          key: "latestCorrectionDt",
          label: "แก้ไขล่าสุด",
          class: "text-nowrap small",
        },
        { key: "dfStatus", label: "สถานะ" },
        { key: "action", label: "", class: "text-center report-export-skip" },
      ],
    };
  },
  created() {
    this.dateRange = [this.today, this.today];
  },
  computed: {
    ...mapGetters({
      doctorsList: "moduleUser/getDoctorList",
      isAccessMenu: "moduleUser/getIsAccessMenu",
      userInfo: "moduleUser/getUserInfo",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      isFileStorageExpired: "moduleBranch/isFileStorageExpired",
      branchInfo: "moduleBranch/getBranchInfo",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    doctorFiltered() {
      if (!this.selectedDoctor) return this.receiptItemList;
      return this.receiptItemList.filter(
        r => r.doctorFullName === this.selectedDoctor
      );
    },
    filteredItems() {
      return this.doctorFiltered.filter(item => {
        if (this.view === "normal" && item.hasDfCorrection) return false;
        if (this.view === "corrected" && !item.hasDfCorrection) return false;
        return matchesSearchQuery(item, this.searchTable, [
          "patientFullName",
          "dn",
          "receiptNo",
          "doctorFullName",
          "itemName",
        ]);
      });
    },
    counts() {
      return {
        all: this.doctorFiltered.length,
        normal: this.doctorFiltered.filter(r => !r.hasDfCorrection).length,
        corrected: this.doctorFiltered.filter(r => r.hasDfCorrection).length,
      };
    },
    viewTabs() {
      return [
        { id: "all", label: "ทั้งหมด", count: this.counts.all },
        { id: "normal", label: "ปกติ", count: this.counts.normal },
        {
          id: "corrected",
          label: "แก้ไขแล้ว",
          count: this.counts.corrected,
          dot: "#d97706",
        },
      ];
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.perPage));
    },
    today() {
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    dateFrom() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").format()
        : "";
    },
    dateTo() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").format()
        : "";
    },
  },
  watch: {
    searchTable() {
      this.currentPage = 1;
    },
    view() {
      this.currentPage = 1;
    },
    selectedDoctor() {
      this.currentPage = 1;
    },
  },
  methods: {
    ...mapActions({
      fetchReceiptItems: "moduleDfCorrection/fetchReceiptItems",
    }),
    formatDate(date) {
      return date ? moment(date).locale("th").format("D MMM YY") : "";
    },
    formatTime(date) {
      return date ? moment(date).locale("th").format("HH:mm") : "";
    },
    formatMoney(num) {
      return Number(num || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    sum(key) {
      const num = this.filteredItems.reduce(
        (accum, item) => accum + (parseFloat(item[key]) || 0),
        0
      );
      return Math.round(num * 100) / 100;
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    setToday() {
      this.dateRange = [this.today, this.today];
    },
    setView(id) {
      this.view = id;
    },
    resetFilters() {
      this.selectedDoctor = null;
      this.searchTable = "";
      this.view = "all";
    },
    isPartialPaid(item) {
      const paid = Number(item.paid) || 0;
      const amount = Number(item.amount) || 0;
      return paid > 0 && Math.abs(paid - amount) >= 0.01;
    },
    isPastCutoff(item) {
      return item.isEditableForCorrection === false;
    },
    isWithinDfGraceWindow(item) {
      // Phase 0: unsubscribed users can only edit DF for receipts created
      // within DF_CORRECTION_GRACE_DAYS. The reference field is creationDt
      // (matches the row display at template line 167).
      const raw = item && item.creationDt;
      if (!raw) return true; // missing date → fail open; BE remains authoritative
      const itemDate = moment(raw);
      if (!itemDate.isValid()) return true;
      const cutoff = moment().subtract(DF_CORRECTION_GRACE_DAYS, "days");
      return itemDate.isSameOrAfter(cutoff);
    },
    isLockedByExpiredSubscription(item) {
      return this.isFileStorageExpired && !this.isWithinDfGraceWindow(item);
    },
    isCorrectionDisabled(item) {
      return (
        this.isPartialPaid(item) ||
        this.isPastCutoff(item) ||
        this.isLockedByExpiredSubscription(item)
      );
    },
    rowDisabledReason(item) {
      if (this.isPartialPaid(item)) {
        return "รายการนี้จ่ายเพียงบางส่วน — ไม่สามารถแก้ DF ได้";
      }
      if (this.isPastCutoff(item)) {
        return "เกินรอบปิดบัญชี — ไม่สามารถแก้ DF ได้";
      }
      if (this.isLockedByExpiredSubscription(item)) {
        return `สมัคร subscription เพื่อแก้ไขรายการที่เก่ากว่า ${DF_CORRECTION_GRACE_DAYS} วัน`;
      }
      return "";
    },
    doctorInitials(name) {
      if (!name) return "?";
      const cleaned = name
        .replace(/^(ทพ\.|ทพญ\.|นพ\.|พญ\.|ดร\.|Dr\.?)\s*/i, "")
        .trim();
      const parts = cleaned.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
      }
      if (parts.length === 1) return parts[0].substring(0, 2);
      return "?";
    },
    async fetchReport() {
      if (!this.dateRange[0] || !this.dateRange[1]) return;
      this.tableBusy = true;
      this.isLoading = true;
      try {
        const res = await this.fetchReceiptItems({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          startDt: this.dateFrom,
          endDt: this.dateTo,
        });
        this.receiptItemList = res.data.report || res.data || [];
        this.currentPage = 1;
        this.lastUpdated = moment().format("HH:mm");
      } catch (err) {
        this.$refs.Dialog.showToast("danger", "ไม่สามารถโหลดข้อมูลได้");
      } finally {
        this.tableBusy = false;
        this.isLoading = false;
      }
    },
    openCorrectionModal(item) {
      // Defense — `:disabled` on the trigger already prevents this in normal
      // flow, but cover programmatic/keyboard activation that might bypass it.
      if (this.isLockedByExpiredSubscription(item)) {
        this.$refs.Dialog.showToast(
          "warning",
          `สมัคร subscription เพื่อแก้ไขรายการที่เก่ากว่า ${DF_CORRECTION_GRACE_DAYS} วัน`
        );
        return;
      }
      this.$refs.dfCorrectionModal.show(item, this.doctorsList);
    },
    openHistoryModal(item) {
      this.$refs.dfCorrectionHistoryModal.show(item);
    },
    onCorrectionSaved() {
      this.fetchReport();
      this.$refs.Dialog.showToast("success", "บันทึกการแก้ไข DF สำเร็จ");
    },
    onError(msg) {
      this.$refs.Dialog.showToast("danger", msg);
    },
    async excelExport() {
      const prevPerPage = this.perPage;
      this.perPage = null;
      await this.$nextTick();
      try {
        exportTableToExcel({
          tableId: "dfCorrectionTable",
          reportTitle: "รายงานการแก้ไข DF",
          reportNameEn: "DF_Correction_Report",
          dateFrom: this.dateRange[0],
          dateTo: this.dateRange[1],
          branchName: this.branchInfo && this.branchInfo.name,
          clinicName: this.clinicInfo && this.clinicInfo.name,
        });
      } finally {
        this.perPage = prevPerPage;
      }
    },
    async pdfExport() {
      const prevPerPage = this.perPage;
      this.perPage = null;
      await this.$nextTick();
      try {
        exportReportToPdf({
          tableId: "dfCorrectionTable",
          reportTitle: "รายงานการแก้ไข DF",
          reportNameEn: "DF_Correction_Report",
          dateFrom: this.dateRange[0],
          dateTo: this.dateRange[1],
          branchName: this.branchInfo && this.branchInfo.name,
          clinicName: this.clinicInfo && this.clinicInfo.name,
          orientation: "landscape",
        });
      } finally {
        this.perPage = prevPerPage;
      }
    },
  },
};
</script>

<style scoped>
.df-correction-page {
  --bg: #f4f6f9;
  --card: #ffffff;
  --ink: #0f1b2d;
  --ink-2: #2a3950;
  --muted: #6b7a90;
  --muted-2: #8a96a8;
  --line: #e3e8ef;
  --line-2: #eef1f6;
  --primary: #2563eb;
  --primary-600: #1d4ed8;
  --primary-50: #eff4ff;
  --warning: #b45309;
  --warning-bg: #fef3c7;
  --warning-border: #fde68a;
  --chip-bg: #f1f4f9;
  --shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  color: var(--ink);
  padding: 22px 28px 64px;
}

/* Page header */
.page-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}
.page-head .crumbs {
  font-size: 12.5px;
  color: var(--muted);
  display: flex;
  gap: 6px;
  align-items: center;
  margin-bottom: 6px;
}
.page-head .crumbs i {
  font-size: 9px;
  opacity: 0.55;
}
.page-head .crumbs-current {
  color: var(--ink-2);
}
.page-head h1 {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 10px;
}
.page-head h1 .title-fa {
  color: var(--ink);
  font-size: 18px;
}
.page-head .lede {
  color: var(--muted);
  font-size: 13px;
  margin-top: 4px;
}
.page-head .head-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  background: var(--chip-bg);
  color: var(--ink-2);
  font-size: 12px;
  border: 1px solid var(--line);
  white-space: nowrap;
}
.pill i {
  font-size: 10px;
  color: var(--muted);
}
.pill i.status-ok {
  color: #10b981;
}

/* Card */
.df-card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

/* Filter bar */
.filter-bar {
  padding: 14px 16px;
  border-bottom: 1px solid var(--line);
  background: linear-gradient(180deg, #fafbfd, #ffffff);
}
.filter-grid {
  display: grid;
  grid-template-columns: 1.4fr 1.1fr 1.4fr auto;
  gap: 10px 14px;
  align-items: end;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}
.field label {
  font-size: 11.5px;
  font-weight: 500;
  color: var(--muted);
  letter-spacing: 0.01em;
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
}
.field label i {
  font-size: 10px;
  opacity: 0.7;
}
.ctrl {
  height: 36px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  padding: 0 12px;
  font: inherit;
  font-size: 13.5px;
  color: var(--ink);
  outline: none;
  transition: border-color 0.12s, box-shadow 0.12s;
  width: 100%;
}
.ctrl::placeholder {
  color: var(--muted-2);
}
.ctrl:hover {
  border-color: #cdd5e0;
}
.ctrl:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}
.ctrl-wrap {
  position: relative;
}
.ctrl-wrap > i.lead {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  font-size: 12px;
  pointer-events: none;
  z-index: 1;
}
.ctrl-wrap > i.lead + .ctrl {
  padding-left: 32px;
}
.ctrl-wrap > .trail {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  font-size: 12px;
}
.ctrl-wrap > .trail.clearable {
  cursor: pointer;
  pointer-events: auto;
}
.select-wrap::after {
  content: "\f078";
  font-family: "Font Awesome 5 Free";
  font-weight: 900;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  font-size: 10px;
  pointer-events: none;
}
select.ctrl {
  appearance: none;
  -webkit-appearance: none;
  background: #fff;
  padding-right: 32px;
}

/* Date range wrap (around vue2-datepicker) */
.date-range-wrap {
  position: relative;
  display: flex;
  align-items: center;
  height: 36px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  padding: 0 8px 0 0;
}
.date-range-wrap:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}
.date-range-wrap.is-invalid {
  border-color: #fca5a5;
}
.date-range-wrap >>> .mx-datepicker,
.date-range-wrap >>> .mx-datepicker-range {
  width: 100%;
  flex: 1;
}
.date-range-wrap >>> .mx-input-wrapper {
  border: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
}
.date-range-wrap >>> .df-date-input,
.date-range-wrap >>> .df-date-input:hover,
.date-range-wrap >>> .df-date-input:focus {
  border: 0 !important;
  box-shadow: none !important;
  outline: none !important;
  background: transparent !important;
  border-radius: 0 !important;
  height: 34px;
  font-size: 13.5px;
  color: var(--ink);
  padding-left: 12px;
  width: 100%;
}
.date-range-wrap >>> .mx-input-wrapper .mx-icon-calendar,
.date-range-wrap >>> .mx-input-wrapper .mx-icon-clear {
  color: var(--muted);
}
.date-quick {
  font-size: 11.5px;
  color: var(--primary);
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  background: var(--primary-50);
  white-space: nowrap;
  margin-left: 4px;
}
.date-quick:hover {
  background: #e0eaff;
}

/* Buttons */
.btn {
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font: inherit;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: background 0.12s, border-color 0.12s, color 0.12s,
    box-shadow 0.12s;
  white-space: nowrap;
}
.btn-primary {
  background: var(--primary);
  color: #fff;
  box-shadow: 0 1px 2px rgba(37, 99, 235, 0.25);
}
.btn-primary:hover {
  background: var(--primary-600);
}
.btn-primary:active {
  transform: translateY(1px);
}
.btn-secondary {
  background: #fff;
  color: var(--ink-2);
  border-color: var(--line);
}
.btn-secondary:hover {
  background: var(--line-2);
  border-color: #cdd5e0;
}
.btn-xs {
  height: 28px;
  padding: 0 10px;
  font-size: 12.5px;
  border-radius: 6px;
}
.btn-icon {
  width: 28px;
  padding: 0;
  justify-content: center;
}
.btn-outline {
  background: #fff;
  color: var(--primary);
  border-color: #c7d6f5;
}
.btn-outline:hover {
  background: var(--primary-50);
}
.btn-outline:disabled,
.btn-outline:disabled:hover {
  background: #f4f6f9;
  color: var(--muted-2);
  border-color: var(--line);
  cursor: not-allowed;
  opacity: 0.7;
}
.btn-outline-info {
  background: #fff;
  color: #0e7490;
  border-color: #bae6fd;
  margin-left: 6px;
}
.btn-outline-info:hover {
  background: #f0f9ff;
}
.field.actions {
  flex-direction: row;
  gap: 8px;
  align-items: end;
}

/* Quick chips row */
.quick-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--line);
  background: #fff;
  flex-wrap: wrap;
}
.quick-row .label-text {
  font-size: 11.5px;
  color: var(--muted);
  margin-right: 4px;
}
.chip {
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: #fff;
  font-size: 12px;
  color: var(--ink-2);
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  user-select: none;
}
.chip:hover {
  background: var(--line-2);
}
.chip.active {
  background: var(--primary-50);
  color: var(--primary-600);
  border-color: #b9cef9;
}
.chip-count {
  color: var(--muted);
  font-weight: 500;
}
.chip.active .chip-count {
  color: var(--primary-600);
}

/* Table toolbar */
.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid var(--line);
  gap: 12px;
}
.table-toolbar .left {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--muted);
}
.table-toolbar .left strong {
  color: var(--ink);
  font-weight: 600;
}

/* Table */
.tbl-wrap {
  overflow: hidden;
}
.df-tbl >>> table.b-table {
  margin: 0;
  font-size: 13px;
}
.df-tbl >>> thead th {
  background: #f7f9fc !important;
  color: var(--muted);
  font-weight: 500;
  font-size: 11.5px;
  text-align: left;
  padding: 9px 12px;
  border-bottom: 1px solid var(--line);
  border-top: 0;
  white-space: nowrap;
  letter-spacing: 0.03em;
}
.df-tbl >>> tbody td {
  padding: 11px 12px;
  border-bottom: 1px solid var(--line-2);
  border-top: 0;
  color: var(--ink-2);
  vertical-align: middle;
}
.df-tbl >>> tbody tr:hover td {
  background: #fafbfd;
}
.df-tbl >>> td.col-idx,
.df-tbl >>> th.col-idx {
  color: var(--muted-2);
  width: 36px;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.mono {
  font-family: "IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Consolas,
    monospace;
  font-size: 12px;
}
.name {
  color: var(--ink);
  font-weight: 500;
}
.num {
  font-variant-numeric: tabular-nums;
}
.df-num {
  color: var(--ink);
  font-weight: 500;
}
.df-prev {
  color: var(--muted);
  text-decoration: line-through;
  font-variant-numeric: tabular-nums;
}
.df-corrected {
  color: var(--warning);
  font-weight: 600;
}
.last-edited {
  color: var(--muted);
  font-size: 11.5px;
  white-space: nowrap;
  display: inline-flex;
  gap: 4px;
  align-items: baseline;
}
.last-edited-time {
  color: var(--muted-2);
  font-variant-numeric: tabular-nums;
}
.muted-dash {
  color: var(--muted-2);
}
.doctor-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--primary-50);
  color: var(--primary);
  display: grid;
  place-items: center;
  font-size: 10.5px;
  font-weight: 600;
  flex-shrink: 0;
}

/* Status badges */
.badge-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 500;
  border: 1px solid transparent;
}
.badge-warn {
  background: var(--warning-bg);
  color: var(--warning);
  border-color: var(--warning-border);
  cursor: pointer;
}
.badge-warn:hover {
  filter: brightness(0.97);
}
.badge-ok {
  background: #f3f4f6;
  color: #4b5563;
  border-color: #e5e7eb;
}
.badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}
.badge-warn .badge-dot {
  background: #d97706;
}
.badge-ok .badge-dot {
  background: #9ca3af;
}

/* Empty */
.empty {
  padding: 56px 24px;
  text-align: center;
}
.empty .ic {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--line-2);
  color: var(--muted);
  display: grid;
  place-items: center;
  margin: 0 auto 12px;
  font-size: 16px;
}
.empty h4 {
  margin: 0 0 4px;
  font-size: 14px;
  color: var(--ink);
  font-weight: 600;
}
.empty p {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
}

/* Footer */
.tbl-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-top: 1px solid var(--line);
  font-size: 12.5px;
  color: var(--muted);
}
.df-pager >>> .page-link {
  border-radius: 6px !important;
  margin: 0 2px;
  border-color: var(--line);
  color: var(--ink-2);
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.df-pager >>> .page-item.active .page-link {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}
.df-pager >>> .page-item.disabled .page-link {
  color: var(--muted-2);
}

/* Info strip */
.info-strip {
  margin-top: 16px;
  font-size: 12.5px;
  color: var(--muted);
  display: flex;
  gap: 10px;
  align-items: center;
}

/* Responsive */
@media (max-width: 1100px) {
  .filter-grid {
    grid-template-columns: 1fr 1fr;
  }
  .filter-grid > .actions {
    grid-column: 1 / -1;
    justify-self: end;
  }
}
@media (max-width: 720px) {
  .df-correction-page {
    padding: 14px 14px 40px;
  }
  .filter-grid {
    grid-template-columns: 1fr;
  }
  .page-head {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
